import { Settings } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  renewEditing,
  setScenarioStatus,
} from '../../../../../../shared/api/scenarioApi';
import {
  EDITING_LOCK_TIMEOUT_MINUTES,
  EDITING_WARNING_MINUTES,
} from '../../../../../../shared/constants/scenarioConstants';
import { useScenario } from '../../../../../../shared/contexts/ScenarioContext';
import { getScenarioHighlightColor } from '../../../../../../shared/utils/scenarioHighlight';
import DoubleConfirmModal from '../../../../../../shared/components/modals/DoubleConfirmModal';

interface ScenarioBannerProps {
  onOpenManager: () => void;
  onUndoVoyages?: () => Promise<void>;
  onUndoStock?: () => Promise<void>;
  onUndoAll?: () => Promise<void>;
}

type ConfirmTarget = 'voyages' | 'stock' | 'all' | null;

const CONFIRM_CONFIG: Record<
  Exclude<ConfirmTarget, null>,
  { title: string; body: string; confirmText: string }
> = {
  voyages: {
    title: 'Undo All Voyage Scenario Changes',
    body: 'This will revert all voyage and shipment scenario edits. This cannot be undone.',
    confirmText: 'Undo All Voyages',
  },
  stock: {
    title: 'Undo All Stock Scenario Changes',
    body: 'This will revert all stock scenario edits. This cannot be undone.',
    confirmText: 'Undo All Stock',
  },
  all: {
    title: 'Undo All Scenario Changes',
    body: 'This will revert ALL voyage, shipment, and stock scenario edits. This cannot be undone.',
    confirmText: 'Undo Everything',
  },
};

const formatCountdown = (totalSeconds: number): string => {
  const clamped = Math.max(0, totalSeconds);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const ScenarioBanner = ({
  onOpenManager,
  onUndoVoyages,
  onUndoStock,
  onUndoAll,
}: ScenarioBannerProps) => {
  const {
    activeScenario,
    clearScenario,
    setActiveScenario,
    editingTimerReset,
    resetEditingTimer,
  } = useScenario();
  const [isExiting, setIsExiting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget>(null);
  const [isUndoing, setIsUndoing] = useState(false);

  const isEditing = activeScenario?.mode === 'EDITING';

  const [secondsLeft, setSecondsLeft] = useState(
    EDITING_LOCK_TIMEOUT_MINUTES * 60
  );
  const [showExtendPrompt, setShowExtendPrompt] = useState(false);
  const extendPromptShownRef = useRef(false);

  useEffect(() => {
    if (!isEditing) {
      extendPromptShownRef.current = false;
      return;
    }
    setSecondsLeft(EDITING_LOCK_TIMEOUT_MINUTES * 60);
    extendPromptShownRef.current = false;
    setShowExtendPrompt(false);
  }, [isEditing, editingTimerReset]);

  useEffect(() => {
    if (!isEditing) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        const next = prev - 1;
        if (
          next <= EDITING_WARNING_MINUTES * 60 &&
          next > 0 &&
          !extendPromptShownRef.current
        ) {
          extendPromptShownRef.current = true;
          setShowExtendPrompt(true);
        }
        return Math.max(0, next);
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isEditing, editingTimerReset]);

  const handleExtend = useCallback(async () => {
    if (!activeScenario) return;
    try {
      await renewEditing(activeScenario.id);
      resetEditingTimer();
    } catch {
      // best-effort
    }
    setShowExtendPrompt(false);
  }, [activeScenario, resetEditingTimer]);

  const handleDeclineExtend = useCallback(async () => {
    if (!activeScenario) return;
    setShowExtendPrompt(false);
    try {
      await renewEditing(activeScenario.id);
      await setScenarioStatus(activeScenario.id, 'VIEW');
      setActiveScenario({
        id: activeScenario.id,
        name: activeScenario.name,
        mode: 'VIEW',
      });
    } catch {
      // best-effort
    }
  }, [activeScenario, setActiveScenario]);

  if (!activeScenario) return null;

  const handleExit = async () => {
    setIsExiting(true);
    try {
      await setScenarioStatus(activeScenario.id, 'DRAFT');
      clearScenario();
    } finally {
      setIsExiting(false);
    }
  };

  const handleConfirmUndo = async () => {
    if (!confirmTarget || isUndoing) return;
    const handler =
      confirmTarget === 'voyages'
        ? onUndoVoyages
        : confirmTarget === 'stock'
          ? onUndoStock
          : onUndoAll;
    if (!handler) return;
    setIsUndoing(true);
    try {
      await handler();
    } finally {
      setIsUndoing(false);
      setConfirmTarget(null);
    }
  };

  const isBusy = isExiting || isUndoing;
  const config = confirmTarget ? CONFIRM_CONFIG[confirmTarget] : null;
  const isTimerCritical =
    isEditing && secondsLeft <= EDITING_WARNING_MINUTES * 60;

  return (
    <>
      <Alert
        severity='warning'
        icon={false}
        sx={{
          alignItems: 'center',
          position: 'fixed',
          top: '64px',
          left: 0,
          right: 0,
          zIndex: 100,
          borderLeft: '4px solid',
          borderColor: (theme) => getScenarioHighlightColor(theme),
          borderRadius: 0,
          boxShadow: 4,
          py: 0.75,
        }}
        action={
          <Stack direction='row' px={2} spacing={1} alignItems='center'>
            {isEditing && (
              <Chip
                icon={<TimerOutlinedIcon sx={{ fontSize: 14 }} />}
                label={formatCountdown(secondsLeft)}
                size='small'
                color={isTimerCritical ? 'error' : 'warning'}
                variant='filled'
                sx={{ fontWeight: 600, fontFamily: 'monospace' }}
              />
            )}
            {isEditing && onUndoVoyages && (
              <Button
                color='inherit'
                size='small'
                variant='outlined'
                disabled={isBusy}
                onClick={() => setConfirmTarget('voyages')}
                startIcon={<ReplayOutlinedIcon fontSize='small' />}
              >
                Undo Voyages
              </Button>
            )}
            {isEditing && onUndoStock && (
              <Button
                color='inherit'
                size='small'
                variant='outlined'
                disabled={isBusy}
                onClick={() => setConfirmTarget('stock')}
                startIcon={<ReplayOutlinedIcon fontSize='small' />}
              >
                Undo Stock
              </Button>
            )}
            {isEditing && onUndoAll && (
              <Button
                color='inherit'
                size='small'
                variant='outlined'
                disabled={isBusy}
                onClick={() => setConfirmTarget('all')}
                startIcon={<ReplayOutlinedIcon fontSize='small' />}
              >
                Undo All
              </Button>
            )}
            <Button
              color='inherit'
              size='small'
              variant='outlined'
              onClick={onOpenManager}
              startIcon={<Settings fontSize='small' />}
            >
              Manage Scenarios
            </Button>
            <Button
              color='warning'
              size='small'
              variant='contained'
              disabled={isBusy}
              onClick={handleExit}
              startIcon={
                isExiting ? (
                  <CircularProgress size={14} color='inherit' />
                ) : (
                  <StopRoundedIcon />
                )
              }
            >
              Exit Scenario
            </Button>
          </Stack>
        }
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <ScienceOutlinedIcon
            fontSize='small'
            sx={{ color: 'warning.dark' }}
          />
          <Stack>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Typography variant='body2' fontWeight={500}>
                Scenario Mode: <strong>{activeScenario.name}</strong>
              </Typography>
              <Chip
                icon={
                  isEditing ? (
                    <EditOutlinedIcon sx={{ fontSize: 12 }} />
                  ) : (
                    <VisibilityOutlinedIcon sx={{ fontSize: 12 }} />
                  )
                }
                label={isEditing ? 'Editing' : 'View Only'}
                size='small'
                color={isEditing ? 'warning' : 'info'}
                variant='filled'
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            </Stack>
            <Typography
              variant='caption'
              sx={{ color: 'warning.dark', opacity: 0.85 }}
            >
              {isEditing
                ? 'You are in editing mode — changes will be saved as scenario deltas.'
                : 'View-only mode — enter editing mode to make changes.'}
            </Typography>
          </Stack>
        </Stack>
      </Alert>

      {config && (
        <DoubleConfirmModal
          open={!!confirmTarget}
          title={config.title}
          confirmText={config.confirmText}
          onConfirm={handleConfirmUndo}
          onCancel={() => setConfirmTarget(null)}
          onClose={() => setConfirmTarget(null)}
          isLoading={isUndoing}
          isDisabled={isUndoing}
          contextText={config.body}
        />
      )}

      <DoubleConfirmModal
        open={showExtendPrompt}
        title='Editing Session Expiring'
        confirmText='Extend'
        contextText={`Your editing session will expire in ${formatCountdown(secondsLeft)}. Would you like to extend it by ${EDITING_LOCK_TIMEOUT_MINUTES} minutes?`}
        onConfirm={handleExtend}
        onCancel={handleDeclineExtend}
        onClose={handleDeclineExtend}
      />
    </>
  );
};

export default ScenarioBanner;
